import ApperIcon from '@/components/ApperIcon';
import { G, text, long, pick } from '@/canvas/defs/base';
import { OPT, PANEL_LINE } from '@/canvas/spine';
import { FieldCell } from '@/components/canvas/CanvasForm';
import { familyOf } from '@/components/canvas/bits';
import { cn } from '@/lib/utils';

function entryFields(def, mode) {
  const ns = def.NORTHSTAR || {};
  const fields = [
    text('so.reviewer', mode === 'panel' ? 'Reviewer role' : 'Sceptic who reviewed it', {
      placeholder: def.sceptic || 'Who reviewed it',
      w: 2,
      guide: G(
        mode === 'panel' ? 'Write the role of the reviewer who asked the question.' : 'Write who played the sceptic at your table.',
        ns['so.reviewer'],
        undefined,
        'Use a role, not a first name, so the question stays with the role when people change.',
      ),
    }),
    long('so.objection', 'Objection, in their words', {
      w: 4,
      rows: 2,
      placeholder: 'The question as they asked it',
      guide: G(
        'Write the question the reviewer asked, in their words. Do not tidy it up.',
        ns['so.objection'],
        undefined,
        'A softened objection will not help you when the real committee asks the sharp version.',
      ),
    }),
    long('so.evidence', 'Evidence that answers it, and the cell it sits in', {
      w: 4,
      rows: 2,
      placeholder: 'The proof, and where it lives on the canvas',
      guide: G(
        'Write the evidence that answers the objection, and say which cell of the canvas holds it. If there is none yet, say so.',
        ns['so.evidence'],
        undefined,
        'An answer with no evidence is an opinion. Leave it open rather than pretend.',
      ),
    }),
    text('so.owner', 'Follow-up owner (a role)', {
      w: 2,
      placeholder: 'A role, for example Head of Retail Banking',
      guide: G('Name the role who will follow this up.', ns['so.owner'], undefined, 'A team is not an owner. Name one role.'),
    }),
  ];
  if (mode === 'ledger') {
    fields.push(
      pick('so.status', 'Status', OPT.status, {
        w: 2,
        guide: G(
          'Open until the evidence is in hand. Answered when the sceptic accepts it. Closed when it needs no more follow-up.',
          ns['so.status'],
          'Open objections go to the Buy-in Ledger and, if starred, to the top of Canvas 9.',
        ),
      }),
      pick('so.star', 'Star: raise this first', ['Yes'], {
        w: 2,
        guide: G(
          'Star the one objection you must answer first when you pitch.',
          undefined,
          'Starred open rows are listed first on Canvas 9.',
          'Starring everything. Star at most one per canvas.',
        ),
      }),
    );
  }
  return fields;
}

export default function SignoffBlock({ def }) {
  const so = def.signoff;
  if (!so) return null;
  const mode = so.mode || 'ledger';
  const fam = familyOf(def);
  const questions = def.signoffQuestions || [];
  const title = mode === 'panel' ? 'The panel round' : mode === 'questions' ? 'Your partner round' : 'The sceptic round';
  return (
    <section className={cn('space-y-4 rounded-2xl border border-border border-l-4 bg-card p-4 shadow-xs md:p-5', fam.edgeLeft)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('flex size-8 items-center justify-center rounded-lg text-white', fam.solid)}>
          <ApperIcon name="MessagesSquare" size={16} />
        </span>
        <div>
          <h3 className="font-heading text-base font-semibold leading-tight">{title}</h3>
          <p className="text-xs text-muted-foreground">
            {mode === 'panel' ? 'Reviewers: ' : 'Sceptic: '}
            <span className="font-medium text-foreground">{def.sceptic}</span>
          </p>
        </div>
      </div>
      {mode === 'panel' && <p className="text-sm leading-relaxed text-muted-foreground">{PANEL_LINE}</p>}
      {mode === 'questions' && (
        <p className="text-sm leading-relaxed text-muted-foreground">
          Ask your partner these questions, and listen. There is nothing to type here. Change the answers above where the questions show a gap.
        </p>
      )}
      {questions.length > 0 && (
        <ol className="space-y-2">
          {questions.map((q, i) => (
            <li key={q} className="flex gap-2.5 text-sm leading-relaxed">
              <span className={cn('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold', fam.soft, fam.ink)}>{i + 1}</span>
              <span>{q}</span>
            </li>
          ))}
        </ol>
      )}
      {mode !== 'questions' && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 border-t border-border pt-4 md:grid-cols-4">
          {entryFields(def, mode).map((f) => (
            <FieldCell key={f.id} field={f} fieldKey={f.id} spanClass={f.w === 4 ? 'md:col-span-4' : 'md:col-span-2'} required={false} />
          ))}
        </div>
      )}
    </section>
  );
}
